import React, { useCallback, useEffect, useRef, useState } from "react";
import { Instagram, RefreshCw, CheckCircle2, XCircle, Play } from "lucide-react";
import { crmApi } from "../../lib/crmApi";
import { Field, inputCls } from "./crmUi";

const btnCls = "border border-[#6E1F2C]/30 text-[#6E1F2C] px-4 py-2 text-[10px] tracking-[0.2em] uppercase hover:bg-[#6E1F2C] hover:text-[#FAF5EC] disabled:opacity-50";

export default function InstagramPanel() {
  const [conn, setConn] = useState(null);
  const [connErr, setConnErr] = useState("");
  const [status, setStatus] = useState(null);
  const [freq, setFreq] = useState(6);
  const [enabled, setEnabled] = useState(true);
  const [limit, setLimit] = useState(20);
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [permalinks, setPermalinks] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const pollRef = useRef(null);

  const loadStatus = useCallback(() => crmApi.instagramSyncStatus().then((d) => {
    setStatus(d);
    if (d.state) {
      setFreq(d.state.frequency_hours ?? 6);
      setEnabled(d.state.sync_enabled !== false);
    }
    return d;
  }).catch(() => null), []);

  const verify = useCallback(async () => {
    setBusy("verify");
    try {
      const d = await crmApi.instagramConnection();
      setConn(d.connection);
      setConnErr(d.error || "");
    } catch (e) { setConnErr(e.message); }
    finally { setBusy(""); }
  }, []);

  useEffect(() => { verify(); loadStatus(); crmApi.instagramAnalytics().then(setAnalytics).catch(() => null); }, [verify, loadStatus]);

  useEffect(() => {
    if (status?.state?.sync_running && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const d = await loadStatus();
        if (d && !d.state?.sync_running) { clearInterval(pollRef.current); pollRef.current = null; }
      }, 4000);
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [status?.state?.sync_running, loadStatus]);

  const runSync = async (mode, lim = 0, extra = {}) => {
    setBusy(mode); setMsg("");
    try {
      await crmApi.instagramSync({ mode, limit: lim, ...extra });
      setMsg(`Sync started (${mode}${lim ? `, latest ${lim}` : ""}${extra.since ? `, from ${extra.since}` : ""}) — runs in the background.`);
      setTimeout(loadStatus, 1500);
    } catch (e) { setMsg(e.message); }
    finally { setBusy(""); }
  };

  const importSelected = async () => {
    const links = permalinks.split(/[\n,\s]+/).map((l) => l.trim()).filter(Boolean);
    if (!links.length) { setMsg("Paste one or more Instagram post URLs first."); return; }
    setBusy("selected"); setMsg("");
    try {
      const r = await crmApi.instagramImportSelected({ permalinks: links });
      setMsg(`Selected import: ${r.imported} imported, ${r.updated} updated${r.not_found?.length ? `, not found: ${r.not_found.join(", ")}` : ""}.`);
      setPermalinks("");
      loadStatus();
    } catch (e) { setMsg(e.message); }
    finally { setBusy(""); }
  };

  const saveSettings = async () => {
    setBusy("settings"); setMsg("");
    try {
      await crmApi.instagramSyncSettings({ sync_enabled: enabled, frequency_hours: Number(freq) || 6 });
      setMsg("Sync schedule saved.");
    } catch (e) { setMsg(e.message); }
    finally { setBusy(""); }
  };

  const st = status?.state || {};
  const last = st.last_sync;

  return (
    <div className="bg-white border border-[#6E1F2C]/10 p-5 space-y-4" data-testid="crm-instagram-panel">
      <div className="flex items-center justify-between">
        <p className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] flex items-center gap-2">
          <Instagram size={13} /> Instagram Connection &amp; Sync
        </p>
        <button className={btnCls} onClick={verify} disabled={busy === "verify"} data-testid="ig-verify-btn">
          <RefreshCw size={11} className={`inline mr-1 ${busy === "verify" ? "animate-spin" : ""}`} />Verify
        </button>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-[11px] text-[#4A3F35]" data-testid="ig-connection-info">
        <span className="text-[#7A6B5F]">Connected account</span>
        <span data-testid="ig-account-name">
          {conn?.account_username ? `@${conn.account_username} — ${conn.account_name || ""}` : "—"}
          {conn?.expected_account_ok && <CheckCircle2 size={12} className="inline ml-1 text-[#3F6E2C]" />}
        </span>
        <span className="text-[#7A6B5F]">Business Account ID</span>
        <span data-testid="ig-account-id">{conn?.account_id || "—"}</span>
        <span className="text-[#7A6B5F]">Connection status</span>
        <span data-testid="ig-conn-status">
          {conn?.token_valid
            ? <span className="text-[#3F6E2C]"><CheckCircle2 size={12} className="inline mr-1" />Connected · token valid</span>
            : <span className="text-[#A3141A]"><XCircle size={12} className="inline mr-1" />Not connected</span>}
        </span>
        <span className="text-[#7A6B5F]">Posts on Instagram</span>
        <span>{conn?.media_count ?? "—"}</span>
        <span className="text-[#7A6B5F]">Last verified</span>
        <span data-testid="ig-last-verified">{conn?.last_verified_at ? new Date(conn.last_verified_at).toLocaleString() : "—"}</span>
      </div>
      {connErr && <p className="text-xs text-[#A3141A]" data-testid="ig-conn-error">{connErr}</p>}

      <div className="border-t border-[#6E1F2C]/10 pt-4 space-y-3">
        <div className="grid grid-cols-3 gap-x-6 text-[11px] text-[#4A3F35]" data-testid="ig-library-stats">
          <span>In library: <b data-testid="ig-total-imported">{status?.library?.total ?? "—"}</b></span>
          <span>AI-tagged: <b>{status?.library?.ai_tagged ?? "—"}</b></span>
          <span>Shared with customers: <b>{status?.library?.shared ?? "—"}</b></span>
        </div>
        {st.sync_running && (
          <p className="text-xs text-[#A6822F] animate-pulse" data-testid="ig-sync-running">
            ⏳ Sync running in the background… (WhatsApp replies are unaffected)
          </p>
        )}
        {last && !st.sync_running && (
          <p className="text-[11px] text-[#7A6B5F]" data-testid="ig-last-sync">
            Last sync: {new Date(last.at).toLocaleString()} ({last.mode}) — imported {last.imported},
            updated {last.updated}, skipped {last.skipped}, removed {last.removed || 0}, errors {last.errors}
            {last.error ? ` — ${last.error}` : ""}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <input type="number" min="0" max="1000" value={limit}
                 onChange={(e) => setLimit(e.target.value)}
                 className={`${inputCls} !w-24`} data-testid="ig-sync-limit" title="Latest N posts (0 = all)" />
          <button className={btnCls} disabled={!!busy || st.sync_running}
                  onClick={() => runSync("new_only", Number(limit) || 0)} data-testid="ig-sync-now-btn">
            <Play size={11} className="inline mr-1" />Sync Now (new posts)
          </button>
          <button className={btnCls} disabled={!!busy || st.sync_running}
                  onClick={() => runSync("resync", Number(limit) || 0)} data-testid="ig-resync-btn">
            Re-sync Existing
          </button>
          <button className={btnCls} disabled={!!busy || st.sync_running}
                  onClick={() => runSync("remove_deleted", 0)} data-testid="ig-remove-deleted-btn">
            Remove Deleted Posts
          </button>
        </div>
      </div>

      <div className="border-t border-[#6E1F2C]/10 pt-4 space-y-3">
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#7A6B5F]">Import older posts manually</p>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="From date">
            <input type="date" value={since} onChange={(e) => setSince(e.target.value)}
                   className={`${inputCls} !w-40`} data-testid="ig-import-since" />
          </Field>
          <Field label="To date">
            <input type="date" value={until} onChange={(e) => setUntil(e.target.value)}
                   className={`${inputCls} !w-40`} data-testid="ig-import-until" />
          </Field>
          <button className={btnCls} disabled={!!busy || st.sync_running || !since}
                  onClick={() => runSync("new_only", Number(limit) || 0, { since, until })}
                  data-testid="ig-import-range-btn">
            Import Date Range
          </button>
        </div>
        <div className="flex items-end gap-3">
          <textarea rows={2} className={`${inputCls} flex-1`} placeholder="Paste Instagram post/reel URLs (one per line) to import specific posts"
                    value={permalinks} onChange={(e) => setPermalinks(e.target.value)}
                    data-testid="ig-import-permalinks" />
          <button className={btnCls} disabled={!!busy || st.sync_running}
                  onClick={importSelected} data-testid="ig-import-selected-btn">
            Import Selected
          </button>
        </div>
      </div>

      {analytics && (
        <div className="border-t border-[#6E1F2C]/10 pt-4 space-y-2" data-testid="ig-analytics">
          <p className="text-[10px] tracking-[0.22em] uppercase text-[#7A6B5F]">Reference performance</p>
          <div className="grid grid-cols-4 gap-2 text-[11px] text-[#4A3F35]">
            <span>Virtual consults: <b>{analytics.outcome_totals?.virtual_consult ?? 0}</b></span>
            <span>Site visits: <b>{analytics.outcome_totals?.site_visit ?? 0}</b></span>
            <span>Quotations: <b>{analytics.outcome_totals?.quotation ?? 0}</b></span>
            <span>Confirmed: <b>{analytics.outcome_totals?.confirmed ?? 0}</b></span>
          </div>
          {analytics.top_shared?.length > 0 && (
            <div className="text-[11px] text-[#7A6B5F]">
              Most shared: {analytics.top_shared.slice(0, 3).map((t) => `${t.title?.slice(0, 30)} (${t.times_sent}×)`).join(" · ")}
            </div>
          )}
        </div>
      )}

      <div className="border-t border-[#6E1F2C]/10 pt-4 flex flex-wrap items-end gap-4">
        <label className="flex items-center gap-2 text-[11px] text-[#4A3F35]">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
                 data-testid="ig-sync-enabled" />
          Scheduled sync enabled
        </label>
        <Field label="Sync frequency (hours)">
          <input type="number" min="1" max="48" value={freq} onChange={(e) => setFreq(e.target.value)}
                 className={`${inputCls} !w-24`} data-testid="ig-sync-frequency" />
        </Field>
        <button className={btnCls} onClick={saveSettings} disabled={busy === "settings"} data-testid="ig-settings-save">
          Save Schedule
        </button>
      </div>
      {msg && <p className="text-xs text-[#3F6E2C]" data-testid="ig-panel-msg">{msg}</p>}
      <p className="text-[10px] text-[#7A6B5F]">
        Imported posts land in the Library as customer-approved references (images, carousels &amp; reels with
        captions, hashtags and permalinks), are AI-tagged automatically, and the WhatsApp AI recommends them
        from the local library only — it never calls Instagram during customer conversations.
      </p>
    </div>
  );
}
